/*
 * Copyright ish group pty ltd. All rights reserved. https://www.ish.com.au
 * No copying or use of this code is allowed without permission in writing from ish.
 */

import { PortalWebsite, Report, ReportOverlay } from '@api/model';
import { LoadingButton } from '@mui/lab';
import { Button, Grid, Typography } from '@mui/material';
import { FormikProvider, useFormik } from 'formik';
import { FormikErrors } from 'formik/dist/types';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import InstantFetchErrorHandler from 'src/js/api/fetch-errors-handlers/InstantFetchErrorHandler';
import ReportsService from 'src/js/api/services/ReportsService';
import AutosizeInput from 'src/js/components/common/AutosizeInput';
import { setLoadingValue } from 'src/js/redux/actions';
import { showConfirm } from 'src/js/redux/actions/Confirm';
import { deletePortalWebsite, updatePortalWebsite } from 'src/js/redux/actions/PortalWebsite';
import * as yup from 'yup';
import IshOncourseLogoColour from '../../../images/IshOncourseLogoColour.svg';
import { PortalWebsiteFormFields } from '../../models/Portal';
import { useAppDispatch, useAppSelector } from '../../redux/hooks/redux';
import FormField from '../common/fields/FormField';

const validationSchema = yup.object().shape({
  websitePrimaryColor: yup.string().nullable().required('Color is required'),
  subDomain: yup.string().nullable().required('Url is required'),
  websiteName: yup.string().nullable().required('Title is required'),
  tutorFeedbackEmail:  yup.string().nullable().email('Please enter valid email')
});

const reportsValidationSchema = yup.object().shape({
  reportAngelId: yup.number().nullable().required('Report is required'),
  reportOverlayAngelId: yup.number().nullable().required('Report overlay is required'),
});

const Portal = () => {
  const { id } = useParams<any>();
  const portalWebsite = useAppSelector((state) => state.portalWebsite.find(p => p.id === parseInt(id))
    || { subDomain: '', websitePrimaryColor: '4146a4' });
  const isSingleInstance = useAppSelector(state => state.portalWebsite?.length <= 1);
  const portalLogoMaxSize = useAppSelector((state) => state.systemProperties?.portalLogoMaxSize) || 204800;
  const reportingEnabled = useAppSelector((state) => state.settings.reportsConfigured) || false;

  const [reports, setReports] = useState<Report[]>([]);
  const [overlays, setOverlays] = useState<ReportOverlay[]>([]);

  const loading = useAppSelector((state) => state.loading);

  const dispatch = useAppDispatch();

  const syncValues = async (dto: PortalWebsite) => {
    dispatch(setLoadingValue(true));
    const logoFile = await (await fetch(dto.websiteLogo)).blob()
    const websiteLogo = new File([logoFile], portalWebsite.websiteLogoName, { type: logoFile.type });
    resetForm({ values: { ...portalWebsite, websiteLogo } })
    dispatch(setLoadingValue(false));
  };

  const formik = useFormik<PortalWebsiteFormFields>({
    initialValues: {
      ...portalWebsite as any,
    },
    enableReinitialize: true,
    validateOnMount: true,
    validationSchema: reportingEnabled ? validationSchema.concat(reportsValidationSchema) : validationSchema,
    validate: ({ websiteLogo }) => {
      const errorsResult: FormikErrors<PortalWebsiteFormFields> = {};

      if (websiteLogo?.size > portalLogoMaxSize) {
        errorsResult.websiteLogo = `Size must be less than ${Math.round(portalLogoMaxSize / 1024)}kb`;
      }

      return errorsResult;
    },
    onSubmit: async ({  id, websitePrimaryColor, subDomain, websiteLogo, websiteLogoName, websiteName, tutorNotesVisibleByDefault, hideStudentDetails, enableOutcomeMarking, tutorFeedbackEmail, reportOverlayAngelId, reportAngelId }) => {
      const logoData: [any,any] = typeof websiteLogo === 'string'
        ? await fetch(websiteLogo).then(res => res.blob()).then(file => [file, websiteLogoName])
        : [websiteLogo, websiteLogo?.name];

      dispatch(updatePortalWebsite(
        id,
        websiteName,
        websitePrimaryColor,
        subDomain,
        logoData[0],
        logoData[1],
        tutorNotesVisibleByDefault,
        hideStudentDetails,
        enableOutcomeMarking,
        tutorFeedbackEmail,
        ...reportingEnabled ? [reportAngelId, reportOverlayAngelId] : []
      ));
    }
  });

  const {
    dirty,
    values,
    isValid,
    setFieldValue,
    errors,
    resetForm,
    submitForm
  } = formik;

  const customLogo = useAppSelector(state => state.systemProperties.customLogoColour)

  useEffect(() => {
    if (reportingEnabled) {
      ReportsService.getAllFromAngel()
        .then(setReports)
        .catch(err => InstantFetchErrorHandler(dispatch, err))

      ReportsService.getOverlaysFromAngel()
        .then(setOverlays)
        .catch(err => InstantFetchErrorHandler(dispatch, err))
    }
  }, [reportingEnabled])

  useEffect(() => {
    if (portalWebsite?.websiteLogo) {
      syncValues(portalWebsite);
    }
  }, [portalWebsite?.websiteLogo]);

  useEffect(() => {
    if (values.websiteLogo && dirty) {
      if (typeof values.websiteLogo === 'object') {
        const reader = new FileReader();
        reader.onload = () => {
          setFieldValue('logoPreview', reader.result);
        };
        reader.readAsDataURL(values.websiteLogo);
      }
      if (typeof values.websiteLogo === 'string') {
        setFieldValue('logoPreview', values.websiteLogo);
      }
    }
  }, [values.websiteLogo]);

  const onUrlChange = (e) => {
    setFieldValue('subDomain', e.target.value);
  }

  const onClickDelete = () => {
    dispatch(showConfirm({
      confirmButtonText: 'Delete',
      confirmMessage: 'You are about to delete portal instance. You will not be able to use its URL after',
      onConfirm: () => {
        dispatch(deletePortalWebsite(id))
      }
    }))
    
  }
  
  return (
    <FormikProvider
      value={formik}
    >
      <Grid className="p-6" container columnSpacing={3} rowSpacing={2}>
        <Grid item xs={12}>
          <div className="centeredFlex mt-2 mb-1">
            <h4 className="coloredHeaderText m-0">Portal settings</h4>
          </div>
        </Grid>
        <Grid item container xs={8}>
          <Grid item xs={12}>
            <Typography variant="caption" color={errors.subDomain ? 'error' : 'textSecondary'} className="relative">
              Portal site location
            </Typography>
            <div className="d-flex align-items-baseline">
              <Typography>
                https://
              </Typography>
              <Typography variant="body1" component="span">
                <AutosizeInput
                  value={values.subDomain}
                  error={Boolean(errors.subDomain)}
                  onChange={onUrlChange}
                  disabled={loading}
                />
              </Typography>
              <Typography>.skillsoncourse.com.au</Typography>
            </div>
            {Boolean(errors.subDomain) && <Typography sx={{ fontSize: '12px', color: '#f44336' }}>{errors.subDomain}</Typography>}
            <FormField
              type="text"
              name="websiteName"
              label="Portal site title"
              disabled={loading}
            />

            <FormField
              type="file"
              name="websiteLogo"
              label="Portal site logo"
              disabled={loading}
              placeholder="Leave empty to use defauilt logo"
            />

            <FormField
              type="text"
              name="websitePrimaryColor"
              placeholder="Site theme color basis"
              InputProps={{
                startAdornment: <span className="mr-1"><FormField
                  type="color"
                  name="websitePrimaryColor"
                  width={18}
                /></span>
              }}
              label="Main color"
              disabled={loading}
            />

            <FormField
              type="text"
              name="tutorFeedbackEmail"
              label="Email tutor feedback to"
              disabled={loading}
            />

            {
              reportingEnabled && <>
                <FormField
                  type="select"
                  name="reportAngelId"
                  selectValueMark='id'
                  selectLabelMark='name'
                  label="Invoice report template"
                  items={reports}
                  disabled={loading}
                />

                <FormField
                  type="select"
                  name="reportOverlayAngelId"
                  label="Invoice report overlay"
                  selectValueMark='id'
                  selectLabelMark='name'
                  items={overlays}
                  disabled={loading}
                />
              </>
            }

            <FormField
              type="checkbox"
              name="tutorNotesVisibleByDefault"
              label="Tutor notes visible for students"
              className="d-flex"
              disabled={loading}
            />

            <FormField
              type="checkbox"
              name="hideStudentDetails"
              label="Hide student contact details from tutor"
              className="d-flex"
              disabled={loading}
            />

            <FormField
              type="checkbox"
              name="enableOutcomeMarking"
              label="Enable outcome marking in tutor portal"
              className="d-flex"
              disabled={loading}
            />
          </Grid>
        </Grid>
        <Grid item container xs={4}>
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary">Logo preview</Typography>
            <img
              alt="FilePreview"
              src={values.logoPreview || customLogo || IshOncourseLogoColour}
              className="d-block w-100 pt-2"
            />
          </Grid>
        </Grid>
        <Grid item xs={12} className="d-flex justify-content-end">
          {!isSingleInstance && <Button
            onClick={onClickDelete}
            disableElevation
            color="error"
            variant="contained"
            className="mr-2"
            disabled={loading}
          >
            Delete
          </Button>}
          <LoadingButton
            variant="contained"
            color="primary"
            loading={loading}
            disabled={!isValid || !dirty}
            onClick={submitForm}
            disableElevation
          >
            Save
          </LoadingButton>
        </Grid>
      </Grid>
    </FormikProvider>
  );
};

export default Portal;